import {
  apiCreateUserEmail,
  apiVerifyUserEmail,
  fetchCurrentUser,
  selectedCurrentUser,
} from '@shared-core/features/backend'
import { useNotifications } from '@shared-core/features/notifications'
import { useEvent } from '@shared-core/hooks/use-event'
import { useI18N } from '@shared-core/hooks/use-i18n'
import { ScreensList, useNavigation } from '@shared-core/navigation'
import { useAppDispatch, useAppSelector } from '@shared-core/store'
import { AppConfirmCodeInput, type ConfirmCodeInputRef } from '@shared-ui/components/app/confirm-code-input'
import { Button } from '@shared-ui/components/ui/button'
import { Column } from '@shared-ui/components/ui/grid'
import { Header } from '@shared-ui/components/ui/header'
import { ScreenWrapperWithoutScroll } from '@shared-ui/components/ui/screen-wrappers/screen-wrapper'
import { GText } from '@shared-ui/components/ui/text'
import { useAppTheme } from '@shared-ui/theme'
import { useEffect, useRef, useState } from 'react'
import { Keyboard, KeyboardAvoidingView, Platform, TextInput } from 'react-native'
import locales from './index.i18n.json'
import { useCreateStyles } from './styles'

export function ScreenEntryEmail() {
  const styles = useCreateStyles()
  const theme = useAppTheme()
  const { addToast } = useNotifications()
  const codeRef = useRef<ConfirmCodeInputRef>(null)
  const finishTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isCodeStep, setIsCodeStep] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const requestRef = useRef(false)
  const { t } = useI18N(locales)
  const navigation = useNavigation()
  const dispatch = useAppDispatch()
  const currentUser = useAppSelector(selectedCurrentUser)
  const [email, setEmail] = useState(currentUser?.email?.address ?? '')
  const [code, setCode] = useState('')
  const [idempotency, setIdempotency] = useState('')
  const [retryAt, setRetryAt] = useState(0)
  const [remaining, setRemaining] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  // Повторная отправка доступна только после серверного интервала.
  useEffect(() => {
    const update = () => setRemaining(Math.max(0, Math.ceil((retryAt - Date.now()) / 1000)))
    update()
    const timer = setInterval(update, 1000)
    return () => clearInterval(timer)
  }, [retryAt])

  // Новый код заменяет предыдущую операцию подтверждения.
  const sendCode = async () => {
    if (requestRef.current || remaining > 0 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return
    requestRef.current = true
    setError('')
    setIsLoading(true)
    try {
      const response = await apiCreateUserEmail({ data: { email: email.trim() } })

      if (!response?.data) {
        const retryAfter = response?.meta.retryAfter

        if (typeof retryAfter === 'number') setRetryAt(Date.now() + retryAfter * 1000)
        setError(t('send-error'))
        return
      }
      setIsCodeStep(true)
      setIdempotency(response.data.idempotency)
      setRetryAt(Date.now() + response.data.retryAfter * 1000)
      setCode('')
      await dispatch(fetchCurrentUser()).unwrap()
    } catch {
      setError(t('send-error'))
    } finally {
      requestRef.current = false
      setIsLoading(false)
    }
  }

  // Код одноразовый: после неудачной проверки потребуется новая отправка.
  const verify = useEvent(async () => {
    if (requestRef.current || code.length !== 4 || !idempotency || isSuccess) return
    requestRef.current = true
    setError('')
    setIsLoading(true)
    try {
      const response = await apiVerifyUserEmail({ data: { code, idempotency } })

      if (!response?.data?.email.verified) {
        setIdempotency('')
        setError(t('verify-error'))
        return
      }
      await dispatch(fetchCurrentUser()).unwrap()
      setIsSuccess(true)
      Keyboard.dismiss()
      finishTimerRef.current = setTimeout(() => {
        navigation.popTo(ScreensList.HOME_START)
        addToast({ text: t('success'), icon: 'check', isClosable: false, timeout: 3500, override: true })
      }, 650)
    } catch {
      setIdempotency('')
      setError(t('verify-error'))
    } finally {
      requestRef.current = false
      setIsLoading(false)
    }
  })

  // Проверяем код автоматически, как при подтверждении телефона.
  useEffect(() => {
    if (!isCodeStep || !idempotency || code.length !== 4 || isSuccess) return
    const timer = setTimeout(() => void verify(), 250)
    return () => clearTimeout(timer)
  }, [code, idempotency, isCodeStep, isSuccess, verify])

  useEffect(() => {
    if (!isCodeStep || !error || isLoading) return

    const timer = setTimeout(() => setCode(''), 650)
    return () => clearTimeout(timer)
  }, [error, isCodeStep, isLoading])

  useEffect(() => {
    if (isCodeStep) codeRef.current?.focus()
  }, [isCodeStep])

  useEffect(() => {
    return () => {
      if (finishTimerRef.current) clearTimeout(finishTimerRef.current)
    }
  }, [])

  const handleBack = () => {
    if (isLoading || isSuccess) return false

    if (!isCodeStep) return true
    setIsCodeStep(false)
    setCode('')
    setIdempotency('')
    setError('')
    return false
  }
  let field = (
    <TextInput
      accessibilityLabel={t('email')}
      placeholder={t('placeholder')}
      value={email}
      onChangeText={setEmail}
      keyboardType="email-address"
      autoCapitalize="none"
      autoCorrect={false}
      autoFocus
      editable={!isLoading}
      placeholderTextColor={theme.COLOR_TEXT_DISABLED}
      selectionColor={theme.COLOR_TEXT_BRAND}
      style={styles.input}
      onSubmitEditing={() => void sendCode()}
    />
  )

  if (isCodeStep) {
    field = (
      <AppConfirmCodeInput
        ref={codeRef}
        length={4}
        isNumeric
        value={code}
        onChange={(value) => {
          if (isLoading || isSuccess || !idempotency) return
          setError('')
          setCode(value)
        }}
        isError={Boolean(error)}
        isSuccess={isSuccess}
      />
    )
  }

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
  return (
    <ScreenWrapperWithoutScroll style={styles.container}>
      <Header isBackButton exitCross={false} onBackHandler={handleBack} />
      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={styles.body}>
        <Column gap={40} style={styles.content}>
          <Column gap={12}>
            <GText fw={600} style={styles.title}>
              {t(isCodeStep ? 'code-title' : 'title')}
            </GText>
            {!isCodeStep && <GText style={styles.description}>{t('description')}</GText>}
            {isCodeStep && (
              <Column>
                <GText style={styles.description}>{t('sent-prefix')}</GText>
                <GText fw={500} style={styles.email}>
                  {email.trim()}
                </GText>
              </Column>
            )}
          </Column>
          {field}
          {Boolean(error) && <GText isDanger>{error}</GText>}
        </Column>
        {!isCodeStep && (
          <Column style={styles.action}>
            <Button disabled={!isValidEmail || isLoading || remaining > 0} onPress={() => void sendCode()}>
              {remaining > 0 ? t('retry-in', { seconds: remaining }) : t('continue')}
            </Button>
          </Column>
        )}
        {isCodeStep && !isSuccess && (!idempotency || remaining === 0) && (
          <Column style={styles.action}>
            <Button disabled={isLoading || remaining > 0} onPress={() => void sendCode()}>
              {remaining > 0 ? t('retry-in', { seconds: remaining }) : t('resend')}
            </Button>
          </Column>
        )}
      </KeyboardAvoidingView>
    </ScreenWrapperWithoutScroll>
  )
}
